import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ShoppingBag,
  Trash2,
  Clock,
  MapPin,
  User,
  Users,
  Calendar,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Award,
  CheckCircle,
  CheckCircle2,
  ExternalLink,
  Phone,
  RotateCcw,
  UserCheck,
  UserPlus,
  Edit3,
  Check,
  AlertTriangle,
  AlertCircle,
  X,
  Plus,
  CheckSquare,
  Square,
  CreditCard,
  Building2,
  Copy,
  Info,
  QrCode,
  FileText,
  School,
  Coins,
} from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { Button, Seo, Modal, DatePicker, ThankYouOrder, StripePaymentModal, Select } from "../../components";
import { UPLOADS_URL, MY_PERSONS_URL, MY_CREDITS_URL, PUBLIC_SCHOOLS_URL, TRANSFER_PAYMENT_URL, getBankDetails, CREDIT_VALUE_EUR } from "../../features/UrlHelper";
import "./KosikPage.scss";
import { formatDateForBackend, formatDateSk } from "../../features/dateFormaters";

const authHeaders = () => ({
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
  },
});

function KosikPage() {
  const {
    cart,
    cartItems,
    flattenedItems,
    toggleTermInCourse,
    addPersonToTerm,
    removePersonFromTerm,
    updateItemPerson,
    removeFromCart,
    clearCart,
    refreshCartPrices,
    getScheduleKey,
    getOrderPayload,
    paymentMethod,
    setPaymentMethod,
    variableSymbol,
    cartCount,
    totalTermsCount,
    totalChildrenCount,
    termsWithoutChildren,
    cartTotal,
    rawCartTotal,
    appliedCredits,
    setAppliedCredits,
  } = useCart();

  const { isLoggedIn, authLoading, openLoginModal, userEmail, userId, userName, firstName, lastName, refreshCredits, checkAuthStatus } = useAuth();

  // Credits state
  const [userCreditsData, setUserCreditsData] = useState({ totalCredits: 0, credits: [] });
  const [useCreditsActive, setUseCreditsActive] = useState(false);
  const [showCreditsList, setShowCreditsList] = useState(false);

  // Saved persons from backend / profile
  const [savedPersons, setSavedPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);

  // List of public schools for child school selection
  const [schoolsList, setSchoolsList] = useState([]);

  // Person modal state for assigning / creating
  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [activeProductId, setActiveProductId] = useState(null);
  const [activeTermKey, setActiveTermKey] = useState(null);
  const [editingCartItemId, setEditingCartItemId] = useState(null);
  const [personForm, setPersonForm] = useState({
    id: null,
    first_name: "",
    last_name: "",
    birth_date: "",
    phone: "",
    school_id: "",
    school_name: "",
    class_grade: "",
    class_letter: "",
    note: "",
    save_to_profile: true,
  });

  // Modal dialog states
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);

  // Completed order data (prepared payload for BE & summary)
  const [completedOrderData, setCompletedOrderData] = useState(null);
  const [submittingOrder, setSubmittingOrder] = useState(false);

  // Copy feedback state (for bank transfer modal)
  const [copiedField, setCopiedField] = useState(null);

  const selectedPaymentMethod = paymentMethod;
  const setSelectedPaymentMethod = setPaymentMethod;

  const DAYS_OF_WEEK = [
    { value: 1, label: "Pondelok" },
    { value: 2, label: "Utorok" },
    { value: 3, label: "Streda" },
    { value: 4, label: "Štvrtok" },
    { value: 5, label: "Piatok" },
    { value: 6, label: "Sobota" },
    { value: 7, label: "Nedeľa" },
  ];

  // Bank details for Transfer from ENV
  const bankDetails = getBankDetails();

  // Load public schools list for child school select
  useEffect(() => {
    axios
      .get(PUBLIC_SCHOOLS_URL)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setSchoolsList(res.data);
        }
      })
      .catch((err) => console.error("Chyba pri načítaní škôl v košíku:", err));
  }, []);

  // Pri otvorení košíka dotiahnuť aktuálne ceny (počet zostávajúcich lekcií sa v čase mení)
  useEffect(() => {
    refreshCartPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved persons if user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      loadUserPersons();
    }
  }, [isLoggedIn]);

  // Load user credits if user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      axios
        .get(MY_CREDITS_URL, authHeaders())
        .then((res) => {
          const total = parseInt(res.data?.totalCredits || 0, 10);
          setUserCreditsData({
            totalCredits: total,
            credits: Array.isArray(res.data?.credits) ? res.data.credits : [],
          });
        })
        .catch((err) => console.error("Chyba pri načítaní kreditov v košíku:", err));
    } else {
      setUserCreditsData({ totalCredits: 0, credits: [] });
      setUseCreditsActive(false);
      setAppliedCredits(0);
    }
  }, [isLoggedIn, setAppliedCredits]);

  // Maximálny povolený počet kreditov na čerpanie: hodnota kreditov (kredity * CREDIT_VALUE_EUR €) nesmie presiahnuť sumu nákupu ani dostupné kredity
  const maxCreditsAllowed = Math.min(
    userCreditsData.totalCredits,
    Math.floor((rawCartTotal || 0) / CREDIT_VALUE_EUR)
  );

  // Dynamické prepočítanie pri akejkoľvek zmene nákupnej ceny alebo kreditov
  useEffect(() => {
    if (appliedCredits > maxCreditsAllowed) {
      setAppliedCredits(maxCreditsAllowed);
    }
  }, [rawCartTotal, maxCreditsAllowed, appliedCredits, setAppliedCredits]);

  async function loadUserPersons() {
    setLoadingPersons(true);
    try {
      const res = await axios.get(MY_PERSONS_URL, authHeaders());
      setSavedPersons(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.log("Nepodarilo sa načítať osoby používateľa:", e);
    } finally {
      setLoadingPersons(false);
    }
  }

  // Open person assignment modal for a specific term
  const openAssignPersonModal = (productId, termKey, existingPerson = null) => {
    setActiveProductId(productId);
    setActiveTermKey(termKey);
    setEditingCartItemId(existingPerson?.cartItemId || null);
    if (existingPerson) {
      const schoolId = existingPerson.school_id || existingPerson.schoolId || "";
      const matchedSchool = schoolsList.find((s) => String(s.id) === String(schoolId));
      const schoolName = existingPerson.school_name || existingPerson.schoolName || matchedSchool?.name || "";
      setPersonForm({
        id: existingPerson.id || null,
        first_name: existingPerson.firstName || existingPerson.first_name || "",
        last_name: existingPerson.lastName || existingPerson.last_name || "",
        birth_date: existingPerson.birthDate || existingPerson.birth_date || "",
        phone: existingPerson.phone || "",
        school_id: schoolId ? String(schoolId) : "",
        school_name: schoolName,
        class_grade: existingPerson.class_grade || existingPerson.classGrade || "",
        class_letter: existingPerson.class_letter || existingPerson.classLetter || "",
        note: existingPerson.note || "",
        save_to_profile: true,
      });
    } else {
      setPersonForm({
        id: null,
        first_name: "",
        last_name: "",
        birth_date: "",
        phone: "",
        school_id: "",
        school_name: "",
        class_grade: "",
        class_letter: "",
        note: "",
        save_to_profile: true,
      });
    }
    setPersonModalOpen(true);
  };

  // Quick select an existing saved person for a specific term
  const handleQuickSelectPerson = (productId, termKey, person) => {
    const matchedSchool = schoolsList.find((s) => String(s.id) === String(person.school_id));
    addPersonToTerm(productId, termKey, {
      id: person.id,
      firstName: person.first_name,
      lastName: person.last_name,
      birthDate: person.birth_date,
      phone: person.phone,
      school_id: person.school_id || null,
      school_name: person.school_name || matchedSchool?.name || null,
      class_grade: person.class_grade || null,
      class_letter: person.class_letter || null,
      note: person.note || "",
    });
  };

  // Save and assign person to active term
  const handleSavePerson = async (e) => {
    e.preventDefault();
    if (!personForm.first_name.trim() || !personForm.last_name.trim()) {
      alert("Meno a priezvisko dieťaťa sú povinné.");
      return;
    }
    if (!personForm.school_id) {
      alert("Výber základnej školy je povinný.");
      return;
    }
    if (!personForm.class_grade) {
      alert("Výber triedy / ročníka (1 – 9) je povinný.");
      return;
    }
    if (!personForm.class_letter || !personForm.class_letter.trim()) {
      alert("Označenie triedy (napr. A, B, C...) je povinné.");
      return;
    }
    if (!personForm.phone || !personForm.phone.trim()) {
      alert("Telefónne číslo na rodiča je povinné.");
      return;
    }

    const matchedSchool = schoolsList.find((s) => String(s.id) === String(personForm.school_id));
    const finalSchoolName = personForm.school_name || matchedSchool?.name || null;

    const personData = {
      id: personForm.id || `local_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      firstName: personForm.first_name.trim(),
      lastName: personForm.last_name.trim(),
      birthDate: personForm.birth_date,
      phone: personForm.phone.trim(),
      school_id: personForm.school_id ? parseInt(personForm.school_id, 10) : null,
      school_name: finalSchoolName,
      class_grade: personForm.class_grade || null,
      class_letter: personForm.class_letter ? personForm.class_letter.trim().toUpperCase() : null,
      note: personForm.note.trim(),
    };

    // If logged in and save_to_profile is checked, save to backend
    if (isLoggedIn && personForm.save_to_profile) {
      const isExistingBePerson = Boolean(personForm.id && !String(personForm.id).startsWith("local_"));
      try {
        const payload = {
          ...(isExistingBePerson ? { id: personForm.id } : {}),
          user_id: userId,
          first_name: personData.firstName,
          last_name: personData.lastName,
          birth_date: formatDateForBackend(personData.birthDate) || null,
          phone: personData.phone || null,
          school_id: personData.school_id || null,
          school_name: personData.school_name || null,
          class_grade: personData.class_grade || null,
          class_letter: personData.class_letter || null,
        };
        if (isExistingBePerson) {
          // Má ID (existujúca osoba na BE) -> POST (úprava)
          const res = await axios.post(MY_PERSONS_URL, payload, authHeaders());
          if (res.data?.person?.id) {
            personData.id = res.data.person.id;
          }
        } else {
          // Nemá ID (nová osoba) -> PUT (vytvorenie)
          const res = await axios.put(MY_PERSONS_URL, payload, authHeaders());
          if (res.data?.person?.id) {
            personData.id = res.data.person.id;
          } else if (res.data?.id) {
            personData.id = res.data.id;
          }
        }
        loadUserPersons();
      } catch (err) {
        console.error("Chyba pri ukladaní osoby do profilu:", err);
      }
    }

    if (editingCartItemId && typeof updateItemPerson === "function") {
      updateItemPerson(editingCartItemId, personData);
    } else if (activeProductId && activeTermKey) {
      addPersonToTerm(activeProductId, activeTermKey, personData);
    }

    setPersonModalOpen(false);
  };

  // Zoskupenie flat položiek do prehľadných kurzov pre zobrazenie v UI
  const groupedCourses = useMemo(() => {
    const map = new Map();
    cartItems.forEach((item) => {
      if (!map.has(item.productId)) {
        map.set(item.productId, {
          productId: item.productId,
          productName: item.productName,
          productDescription: item.productDescription,
          photo: item.photo,
          price: item.price,
          originalPrice: item.originalPrice,
          hasDiscount: item.hasDiscount,
          cancellation_credits: item.cancellation_credits,
          date_from: item.date_from,
          date_to: item.date_to,
          school_id: item.school_id,
          school_name: item.school_name,
          school_address: item.school_address,
          school_city: item.school_city,
          availableSchedules: item.availableSchedules || [],
          termsMap: new Map(),
        });
      }
      const course = map.get(item.productId);
      const termKey = item.termKey || (item.scheduleId ? `sch_${item.scheduleId}` : `sch_${item.productId}_0`);
      if (!course.termsMap.has(termKey)) {
        course.termsMap.set(termKey, {
          termKey,
          schedule: item.schedule,
          items: [],
          assignedPersons: [],
        });
      }
      const term = course.termsMap.get(termKey);
      term.items.push(item);
      if (item.person) {
        term.assignedPersons.push({
          ...item.person,
          cartItemId: item.cartItemId,
        });
      }
    });

    return Array.from(map.values()).map((course) => ({
      ...course,
      selectedTerms: Array.from(course.termsMap.values()),
    }));
  }, [cartItems]);

  // Open checkout modal or process order depending on chosen payment method
  const handleInitiateCheckout = async () => {
    if (termsWithoutChildren > 0) {
      alert(
        "Pred pokračovaním k platbe prosím priraďte dieťa ku všetkým zvoleným termínom krúžkov."
      );
      return;
    }

    // Pred platbou overiť ceny – ak sa zmenili, košík sa aktualizuje a používateľ musí potvrdiť znova
    setSubmittingOrder(true);
    const { changed, unavailable } = await refreshCartPrices();
    setSubmittingOrder(false);

    if (unavailable.length > 0) {
      const names = [...new Set(unavailable.map((u) => u.productName))].join(", ");
      alert(
        `Niektoré krúžky v košíku už nie sú dostupné na prihlásenie (${names}). Odstráňte ich prosím z košíka.`
      );
      return;
    }

    if (changed.length > 0) {
      const lines = changed
        .map((c) => `• ${c.productName}: ${c.oldPrice.toFixed(2)} € → ${c.newPrice.toFixed(2)} €`)
        .join("\n");
      alert(
        `Cena niektorých krúžkov sa zmenila, pretože medzičasom prebehla lekcia:\n${lines}\n\nKošík bol aktualizovaný. Skontrolujte prosím sumu a pokračujte znova.`
      );
      return;
    }

    if (cartTotal === 0) {
      handleConfirmOrder("transfer");
      return;
    }

    if (selectedPaymentMethod === "card") {
      setCardModalOpen(true);
    } else if (selectedPaymentMethod === "transfer") {
      handleConfirmOrder("transfer");
    }
  };

  // Finalize order confirmation
  const handleConfirmOrder = async (method) => {
    const payload = getOrderPayload(method);
    console.log("📦 Podkladové dáta prihlášky pripravené pre BE (Hlavička + Flat položky):", payload);

    if (method === "transfer" || cartTotal === 0) {
      setSubmittingOrder(true);
      try {
        const endpoint = TRANSFER_PAYMENT_URL;
        const res = await axios.post(endpoint, payload, authHeaders());
        const orderData = {
          ...payload,
          orderId: res.data?.order?.orderNumber,
          variableSymbol: res.data?.order?.variableSymbol ?? payload.variableSymbol,
          dbOrderId: res.data?.order?.id,
          totalAmount: res.data?.order?.totalAmount ?? payload.totalAmount,
          customerEmail: payload.userEmail || userEmail,
          paymentMethod: res.data?.order?.paymentMethod || method,
        };
        setCompletedOrderData(orderData);

        // Aktualizácia kreditov v hlavičke
        if (typeof refreshCredits === "function") {
          refreshCredits();
        }

        // Close payment modals
        setTransferModalOpen(false);
        setCardModalOpen(false);

        //refresh credits data
        checkAuthStatus();

        // Clear cart and scroll to top to reveal Thank You view
        clearCart();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        console.error(`Chyba pri odosielaní objednávky (${method}):`, err);
        alert(
          err.response?.data?.message ||
          "Nepodarilo sa zaznamenať prihlášku. Skontrolujte prosím pripojenie a skúste znova."
        );
      } finally {
        setSubmittingOrder(false);
      }
      return;
    }

    setCompletedOrderData(payload);

    // Close payment modals
    setTransferModalOpen(false);
    setCardModalOpen(false);
    clearCart();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Copy text helper with visual feedback
  const handleCopyText = (text, fieldName) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2500);
    }
  };

  const currentVariableSymbol = "Pridelený v potvrdzujúcom e-maile";
  const recipientMessage = `Prihláška - ${[firstName, lastName].filter(Boolean).join(" ") || userName || "Triferis"
    }`;

  return (
    <div className="kosik-page">
      <Seo
        title="Nákupný košík"
        description="Váš nákupný košík s vybranými krúžkami, termínmi a priradenými deťmi."
        canonical="/kosik"
      />

      {/* ─── Main Content ───────────────────────────── */}
      <section className="kosik-page__main">
        <div className="kosik-page__container">
          {authLoading ? (
            <div className="kosik-page__empty">
              <h2 className="kosik-page__empty-title">Načítavam košík...</h2>
              <p className="kosik-page__empty-desc">Prosím chvíľu strpenia.</p>
            </div>
          ) : completedOrderData ? (
            /* ─── Thank You View ─── */
            <ThankYouOrder
              orderData={completedOrderData}
              onReset={() => setCompletedOrderData(null)}
            />
          ) : !isLoggedIn ? (
            /* ─── Unauthenticated State ─── */
            <div className="kosik-page__empty">
              <div className="kosik-page__empty-icon-wrap">
                <User size={54} />
              </div>
              <h2 className="kosik-page__empty-title">Pre prístup do košíka sa prihláste</h2>
              <p className="kosik-page__empty-desc">
                Nákupný košík a prihlasovanie detí na krúžky sú viazané na váš používateľský účet. Prihláste sa alebo si vytvorte bezplatný účet.
              </p>
              <Button
                variant="primary"
                size="md"
                className="kosik-page__empty-btn"
                onClick={openLoginModal}
              >
                <UserPlus size={16} /> Prihlásiť sa / Registrácia
              </Button>
            </div>
          ) : cartCount === 0 ? (
            /* ─── Empty Cart State ─── */
            <div className="kosik-page__empty">
              <div className="kosik-page__empty-icon-wrap">
                <ShoppingBag size={54} />
              </div>
              <h2 className="kosik-page__empty-title">Váš košík je prázdny</h2>
              <p className="kosik-page__empty-desc">
                Zatiaľ ste si nepridali žiadny krúžok. Pozrite si náš aktuálny
                zoznam krúžkov a pridajte si krúžok pre vaše dieťa.
              </p>
              <Link to="/#ponuka" className="kosik-page__empty-btn">
                <ArrowLeft size={16} /> Prezrieť ponuku krúžkov
              </Link>
            </div>
          ) : (
            /* ─── Cart with Items ─── */
            <div className="kosik-page__grid">
              {/* Left Column: Items List */}
              <div className="kosik-page__items-col">
                <div className="kosik-page__items-header">
                  <h2 className="kosik-page__items-title">
                    Vybrané krúžky ({cartCount})
                  </h2>
                  <button
                    className="kosik-page__clear-btn"
                    onClick={clearCart}
                    title="Odstrániť všetky položky"
                  >
                    <RotateCcw size={14} /> Vyprázdniť košík
                  </button>
                </div>

                <div className="kosik-page__items-list">
                  {groupedCourses.map((item) => {
                    const schedules = item.availableSchedules || [];
                    const selectedTerms = item.selectedTerms || [];

                    return (
                      <div key={item.productId} className="kosik-page__item-card">
                        {/* Course Header Info */}
                        <div className="kosik-page__item-header-row">
                          {/* Photo / Thumbnail */}
                          <div className="kosik-page__item-media">
                            {item.photo ? (
                              <img
                                src={`${UPLOADS_URL}${item.photo}`}
                                alt={item.productName}
                                className="kosik-page__item-img"
                              />
                            ) : (
                              <div className="kosik-page__item-img-placeholder">
                                <ShoppingBag size={24} />
                              </div>
                            )}
                          </div>

                          {/* Basic Info */}
                          <div className="kosik-page__item-header-info">
                            <div className="kosik-page__item-top">
                              <h3 className="kosik-page__item-name">
                                {item.productName}
                              </h3>
                              <button
                                className="kosik-page__item-remove-btn"
                                onClick={() => removeFromCart(item.productId)}
                                title="Odstrániť celý krúžok z košíka"
                                aria-label={`Odstrániť ${item.productName}`}
                              >
                                <Trash2 size={17} />
                              </button>
                            </div>

                            <div className="kosik-page__item-meta-row">
                              {(item.date_from || item.date_to) && (
                                <span className="kosik-page__item-dates">
                                  <Calendar size={12} />
                                  {item.date_from
                                    ? formatDateSk(item.date_from)
                                    : ""}{" "}
                                  —{" "}
                                  {item.date_to
                                    ? formatDateSk(item.date_to)
                                    : ""}
                                </span>
                              )}

                              <span className="kosik-page__item-price-tag">
                                {item.hasDiscount ? (
                                  <>
                                    <span className="kosik-page__price-orig-sm">
                                      {item.originalPrice.toFixed(2)} €
                                    </span>{" "}
                                    <strong>{item.price.toFixed(2)} € / deň</strong>
                                  </>
                                ) : (
                                  <strong>{item.price.toFixed(2)} € / deň</strong>
                                )}
                              </span>
                            </div>

                            {(() => {
                              const matchedSchool = schoolsList.find((s) => String(s.id) === String(item.school_id));
                              const schoolName = item.school_name || matchedSchool?.name;
                              const schoolAddress = item.school_address || matchedSchool?.address;
                              const schoolCity = item.school_city || matchedSchool?.city;
                              const fullPlace = [schoolName, schoolAddress, schoolCity].filter(Boolean).join(", ");
                              if (!fullPlace) return null;
                              return (
                                <div className="kosik-page__item-place-row">
                                  <MapPin size={13} />
                                  <span>
                                    <strong>Miesto konania:</strong> {fullPlace}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* ─── Zoznam termínov pre tento krúžok ─── */}
                        <div className="kosik-page__terms-section">
                          <div className="kosik-page__terms-header">
                            <Clock size={16} />
                            <span>
                              Zvoľte termíny a priraďte deti (môžete zvoliť viac termínov aj viac detí na termín):
                            </span>
                          </div>

                          <div className="kosik-page__terms-checkbox-list">
                            {schedules.map((sch, schIdx) => {
                              const termKey = getScheduleKey(sch, schIdx);
                              const selectedTermObj = selectedTerms.find(
                                (t) => t.termKey === termKey
                              );
                              const isChecked = Boolean(selectedTermObj);
                              const assignedPersons = selectedTermObj?.assignedPersons || [];
                              const isScheduleFull = Boolean(sch.is_full);
                              const spotsLeft = sch.spots_left !== null && sch.spots_left !== undefined ? sch.spots_left : null;
                              const isMaxAssigned = spotsLeft !== null && assignedPersons.length >= spotsLeft;

                              return (
                                <div
                                  key={sch.id || schIdx}
                                  className={`kosik-page__term-row-card ${isChecked
                                    ? "kosik-page__term-row-card--checked"
                                    : ""
                                    }`}
                                >
                                  {/* Term Selection Line */}
                                  <div
                                    className="kosik-page__term-row-header"
                                    onClick={() => {
                                      if (isScheduleFull && !isChecked) {
                                        alert("Tento termín je už plne obsadený.");
                                        return;
                                      }
                                      toggleTermInCourse(item.productId, sch, schIdx);
                                    }}
                                  >
                                    <div className="kosik-page__checkbox-icon">
                                      {isChecked ? (
                                        <CheckSquare
                                          size={20}
                                          className="kosik-page__icon--checked"
                                        />
                                      ) : (
                                        <Square
                                          size={20}
                                          className="kosik-page__icon--unchecked"
                                        />
                                      )}
                                    </div>

                                    <div className="kosik-page__term-row-details">
                                      <div className="kosik-page__term-row-time">
                                        <strong>
                                          {DAYS_OF_WEEK.find(day => Number(day.value) === Number(sch.day))?.label}: {sch.time_from} - {sch.time_to}
                                        </strong>
                                      </div>

                                      <div className="kosik-page__term-row-meta">
                                        {sch.count_lesson ? (
                                          <span className="kosik-page__pill kosik-page__pill--lessons">
                                            {sch.count_lesson} {sch.count_lesson === 1 ? "lekcia" : sch.count_lesson >= 2 && sch.count_lesson <= 4 ? "lekcie" : "lekcií"}
                                          </span>
                                        ) : null}
                                        {sch.trainer && (
                                          <span className="kosik-page__pill">
                                            <User
                                              size={12}
                                              className="kosik-page__icon--purple"
                                            />
                                            Tréner: {sch.trainer}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="kosik-page__term-price-col">
                                      <strong className="kosik-page__term-price-val">
                                        {(sch.schedule_price ?? sch.price ?? item.price).toFixed(2)} €
                                      </strong>
                                      <div className="kosik-page__term-toggle-badge">
                                        {isChecked ? "Zvolený termín" : "+ Zvoliť termín"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ─── Pod každým vybraným termínom: Deti ─── */}
                                  {isChecked && (
                                    <div className="kosik-page__term-person-section">
                                      <div className="kosik-page__term-person-header">
                                        <div className="kosik-page__term-person-label">
                                          <Users size={15} />
                                          <span>
                                            Deti prihlásené na tento termín ({assignedPersons.length}):
                                          </span>
                                          {assignedPersons.length > 0 && (
                                            <strong className="kosik-page__term-subtotal">
                                              (Spolu: {((sch.schedule_price ?? sch.price ?? item.price) * assignedPersons.length).toFixed(2)} €)
                                            </strong>
                                          )}
                                        </div>

                                        <Button
                                          type="button"
                                          variant="secondary"
                                          size="sm"
                                          className="kosik-page__add-child-inline-btn"
                                          onClick={() => {
                                            if (isMaxAssigned && assignedPersons.length > 0) {
                                              alert(`Kapacita termínu bola naplnená (${spotsLeft} voľných miest). Nie je možné pridať ďalšie dieťa.`);
                                              return;
                                            }
                                            openAssignPersonModal(
                                              item.productId,
                                              termKey
                                            );
                                          }}
                                        >
                                          <UserPlus size={13} />{" "}
                                          {assignedPersons.length > 0
                                            ? "+ Pridať ďalšie dieťa"
                                            : "+ Priradiť dieťa"}
                                        </Button>
                                      </div>

                                      {/* Zoznam priradených detí */}
                                      {assignedPersons.length > 0 && (
                                        <div className="kosik-page__children-chips-list">
                                          {assignedPersons.map((person, pIdx) => (
                                            <div
                                              key={person.cartItemId || person.id || pIdx}
                                              className="kosik-page__paired-person-card"
                                            >
                                              <div className="kosik-page__paired-person-icon">
                                                <User size={18} />
                                              </div>
                                              <div className="kosik-page__paired-person-info">
                                                <strong className="kosik-page__paired-person-name">
                                                  {person.firstName} {person.lastName}
                                                </strong>
                                                <div className="kosik-page__paired-person-meta">
                                                  {person.birthDate && (
                                                    <span>
                                                      Nar.:{" "}
                                                      {formatDateSk(
                                                        person.birthDate
                                                      )}
                                                    </span>
                                                  )}
                                                  {(person.school_name || person.school_id || person.class_grade || person.class_letter) && (
                                                    <span className="kosik-page__paired-person-school">
                                                      <School size={12} />
                                                      {[
                                                        person.school_name ||
                                                        (person.school_id &&
                                                          schoolsList.find(
                                                            (s) => String(s.id) === String(person.school_id)
                                                          )?.name),
                                                        (person.class_grade || person.class_letter)
                                                          ? `${person.class_grade ? `${person.class_grade}.` : ""}${person.class_letter || ""} trieda`
                                                          : "",
                                                      ]
                                                        .filter(Boolean)
                                                        .join(" • ")}
                                                    </span>
                                                  )}
                                                  {person.phone && (
                                                    <span>Tel: {person.phone}</span>
                                                  )}
                                                  {person.note && (
                                                    <span className="kosik-page__paired-person-note">
                                                      Pozn.: {person.note}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="kosik-page__paired-person-actions">
                                                <button
                                                  type="button"
                                                  className="kosik-page__person-edit-btn"
                                                  onClick={() =>
                                                    openAssignPersonModal(
                                                      item.productId,
                                                      termKey,
                                                      person
                                                    )
                                                  }
                                                  title="Upraviť údaje dieťaťa"
                                                >
                                                  <Edit3 size={13} /> Zmeniť
                                                </button>
                                                <button
                                                  type="button"
                                                  className="kosik-page__person-remove-btn"
                                                  onClick={() =>
                                                    removePersonFromTerm(
                                                      item.productId,
                                                      termKey,
                                                      person.id || pIdx
                                                    )
                                                  }
                                                  title="Odobrať dieťa z tohto termínu"
                                                >
                                                  <X size={13} />
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Rýchly výber z profilu pre ďalšie / nepriradené deti */}
                                      {savedPersons.filter(
                                        (sp) =>
                                          !assignedPersons.some(
                                            (ap) =>
                                              ap.id === sp.id ||
                                              (ap.firstName?.trim().toLowerCase() ===
                                                sp.first_name?.trim().toLowerCase() &&
                                                ap.lastName?.trim().toLowerCase() ===
                                                sp.last_name?.trim().toLowerCase())
                                          )
                                      ).length > 0 && (
                                          <div className="kosik-page__quick-persons">
                                            <span className="kosik-page__quick-label">
                                              {assignedPersons.length > 0
                                                ? "Priradiť ďalšie dieťa z profilu:"
                                                : "Rýchly výber z profilu:"}
                                            </span>
                                            <div className="kosik-page__quick-buttons">
                                              {savedPersons
                                                .filter(
                                                  (sp) =>
                                                    !assignedPersons.some(
                                                      (ap) =>
                                                        ap.id === sp.id ||
                                                        (ap.firstName?.trim().toLowerCase() ===
                                                          sp.first_name?.trim().toLowerCase() &&
                                                          ap.lastName?.trim().toLowerCase() ===
                                                          sp.last_name?.trim().toLowerCase())
                                                    )
                                                )
                                                .map((p) => (
                                                  <button
                                                    key={p.id}
                                                    type="button"
                                                    className="kosik-page__quick-person-btn"
                                                    onClick={() =>
                                                      handleQuickSelectPerson(
                                                        item.productId,
                                                        termKey,
                                                        p
                                                      )
                                                    }
                                                  >
                                                    <UserPlus size={12} /> {p.first_name}{" "}
                                                    {p.last_name}
                                                  </button>
                                                ))}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="kosik-page__back-link-wrap">
                  <Link to="/#ponuka" className="kosik-page__back-link">
                    <ArrowLeft size={16} /> Pridať ďalší krúžok z ponuky
                  </Link>
                </div>
              </div>

              {/* Right Column: Order Summary Card */}
              <div className="kosik-page__summary-col">
                <div className="kosik-page__summary-card">
                  <h3 className="kosik-page__summary-title">Súhrn prihlášky</h3>
                  <div className="kosik-page__summary-underline" />

                  <div className="kosik-page__summary-lines">
                    <div className="kosik-page__summary-line">
                      <span>Počet krúžkov:</span>
                      <strong>{cartCount}</strong>
                    </div>

                    <div className="kosik-page__summary-line">
                      <span>Počet zvolených termínov:</span>
                      <strong>{totalTermsCount}</strong>
                    </div>

                    <div className="kosik-page__summary-line">
                      <span>Celkový počet detí / prihlášok:</span>
                      <strong className="kosik-page__highlight-count">
                        {totalChildrenCount}
                      </strong>
                    </div>

                    <div className="kosik-page__summary-line">
                      <span>Medzisúčet položiek:</span>
                      <strong>{(rawCartTotal || 0).toFixed(2)} €</strong>
                    </div>

                    {/* ─── Kredity Section ─── */}
                    {isLoggedIn && userCreditsData.totalCredits > 0 && (
                      <div className="kosik-page__credits-box">
                        <div className="kosik-page__credits-header">
                          <div className="kosik-page__credits-header-left">
                            <Coins size={18} className="kosik-page__credits-icon" />
                            <div>
                              <strong className="kosik-page__credits-title">Uplatniť kredity</strong>
                              <span className="kosik-page__credits-avail">
                                K dispozícii: <strong>{userCreditsData.totalCredits}</strong> {userCreditsData.totalCredits === 1 ? "kredit" : userCreditsData.totalCredits <= 4 ? "kredity" : "kreditov"} ({(userCreditsData.totalCredits * CREDIT_VALUE_EUR).toFixed(2)} €)
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="kosik-page__credits-action">
                          <label className="kosik-page__credits-check-label">
                            <input
                              type="checkbox"
                              checked={useCreditsActive}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setUseCreditsActive(checked);
                                if (checked) {
                                  setAppliedCredits(maxCreditsAllowed);
                                } else {
                                  setAppliedCredits(0);
                                }
                              }}
                              className="kosik-page__credits-checkbox"
                            />
                            <span>Použiť kredity na túto objednávku</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {appliedCredits > 0 && (
                      <div className="kosik-page__summary-line kosik-page__summary-line--discount">
                        <span>Uplatnené kredity:</span>
                        <strong className="kosik-page__credits-discount-val">
                          -{(appliedCredits * CREDIT_VALUE_EUR).toFixed(2)} € ({appliedCredits} {appliedCredits === 1 ? "kredit" : appliedCredits <= 4 ? "kredity" : "kreditov"})
                        </strong>
                      </div>
                    )}

                    <div className="kosik-page__summary-divider" />

                    <div className="kosik-page__summary-total">
                      <span>Celková suma na úhradu:</span>
                      <strong className="kosik-page__total-val">
                        {cartTotal.toFixed(2)}€
                      </strong>
                    </div>
                  </div>

                  {/* Status indicators */}
                  {termsWithoutChildren > 0 ? (
                    <div className="kosik-page__status-alert kosik-page__status-alert--warn">
                      <AlertTriangle size={16} />
                      <span>
                        Ešte {termsWithoutChildren} {termsWithoutChildren === 1 ? "termín nemá" : "termíny nemajú"} priradené dieťa. Pre
                        dokončenie priraďte dieťa.
                      </span>
                    </div>
                  ) : (
                    <div className="kosik-page__status-alert kosik-page__status-alert--ok">
                      <CheckCircle size={16} />
                      <span>Všetky zvolené termíny majú priradené dieťa.</span>
                    </div>
                  )}

                  {/* ─── Payment Methods Selection ─── */}
                  {cartTotal === 0 ? (
                    <div className="kosik-page__credits-zero-box">
                      <div className="kosik-page__credits-zero-icon">
                        <Sparkles size={22} />
                      </div>
                      <div className="kosik-page__credits-zero-text">
                        <strong>100% zľava z kreditov</strong>
                        <p>Celá suma objednávky je uhradená vašimi kreditmi. Nie je potrebná žiadna ďalšia platba.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="kosik-page__payment-section">
                      <h4 className="kosik-page__payment-title">
                        Spôsob platby
                      </h4>
                      <p className="kosik-page__payment-subtitle">
                        Zvoľte, ako si želáte prihlášku uhradiť:
                      </p>

                      <div className="kosik-page__payment-options">
                        {/* 2. Bankový prevod */}
                        <div
                          className={`kosik-page__payment-option ${selectedPaymentMethod === "transfer"
                            ? "kosik-page__payment-option--selected"
                            : ""
                            }`}
                          onClick={() => setSelectedPaymentMethod("transfer")}
                        >
                          <div className="kosik-page__payment-option-radio">
                            <span className="kosik-page__radio-dot" />
                          </div>
                          <div className="kosik-page__payment-option-icon kosik-page__payment-option-icon--transfer">
                            <Building2 size={18} />
                          </div>
                          <div className="kosik-page__payment-option-info">
                            <div className="kosik-page__payment-option-header">
                              <strong className="kosik-page__payment-option-name">
                                Bankový prevod
                              </strong>
                              <span className="kosik-page__payment-badge kosik-page__payment-badge--transfer">
                                IBAN / QR
                              </span>
                            </div>
                            <span className="kosik-page__payment-option-desc">
                              Platba vopred na bankový účet OZ s VS
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Primary CTA button */}
                  <button
                    type="button"
                    className={`kosik-page__checkout-btn ${termsWithoutChildren > 0 || submittingOrder
                      ? "kosik-page__checkout-btn--disabled"
                      : ""
                      }`}
                    onClick={handleInitiateCheckout}
                    disabled={termsWithoutChildren > 0 || submittingOrder}
                  >
                    {cartTotal === 0 ? (
                      <>
                        <Sparkles size={18} />
                        <span>
                          {submittingOrder ? "Odosielam prihlášku..." : "Potvrdiť prihlášku (Uhradené kreditmi)"}
                        </span>
                      </>
                    ) : selectedPaymentMethod === "card" ? (
                      <>
                        <CreditCard size={18} />
                        <span>Zaplatiť kartou ({cartTotal.toFixed(2)} €)</span>
                      </>
                    ) : (
                      <>
                        <Building2 size={18} />
                        <span>
                          {submittingOrder ? "Odosielam prihlášku..." : `Záväzná prihláška (${cartTotal.toFixed(2)} €)`}
                        </span>
                      </>
                    )}
                  </button>

                  {/* Trust badges */}
                  {/* <div className="kosik-page__trust-list">
                    <div className="kosik-page__trust-item">
                      <ShieldCheck size={16} className="kosik-page__trust-icon" />
                      <span>Bezpečná registrácia priamo v škole</span>
                    </div>
                    <div className="kosik-page__trust-item">
                      <Award size={16} className="kosik-page__trust-icon" />
                      <span>Všetky potrebné pomôcky sú v cene</span>
                    </div>
                    <div className="kosik-page__trust-item">
                      <CheckCircle size={16} className="kosik-page__trust-icon" />
                      <span>Možnosť čerpania náhradných hodín</span>
                    </div>
                  </div> */}

                  {/* Support Box
                  <div className="kosik-page__support-box">
                    <Phone size={16} />
                    <div>
                      <span>Potrebujete poradiť s prihláškou?</span>
                      <a href="tel:+421 944 526 240" className="kosik-page__phone-link">
                        +421 944 526 240
                      </a>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── Modal for Assigning / Entering Person ─── */}
      <Modal
        isOpen={personModalOpen}
        onClose={() => setPersonModalOpen(false)}
        title="Údaje účastníka / dieťaťa"
      >
        <form onSubmit={handleSavePerson} className="kosik-page__person-form">
          <p className="kosik-page__form-desc">
            Zadajte údaje dieťaťa, pre ktoré bude tento termín krúžku vystavený. Môžete priradiť aj viac detí k rovnakému termínu.
          </p>


          <div className="kosik-page__form-row">
            <div className="kosik-page__form-field">
              <label className="kosik-page__form-label">Meno dieťaťa *</label>
              <input
                type="text"
                className="kosik-page__form-input"
                placeholder="Napr. Janko"
                value={personForm.first_name}
                onChange={(e) =>
                  setPersonForm((prev) => ({ ...prev, first_name: e.target.value }))
                }
                required
              />
            </div>

            <div className="kosik-page__form-field">
              <label className="kosik-page__form-label">Priezvisko dieťaťa *</label>
              <input
                type="text"
                className="kosik-page__form-input"
                placeholder="Napr. Hraško"
                value={personForm.last_name}
                onChange={(e) =>
                  setPersonForm((prev) => ({ ...prev, last_name: e.target.value }))
                }
                required
              />
            </div>
          </div>

          <div className="kosik-page__form-row">
            <DatePicker
              label="Dátum narodenia"
              value={personForm.birth_date}
              placeholder="Vyberte dátum narodenia..."
              onChange={(val) => {
                const v = val?.target?.value !== undefined ? val.target.value : val;
                setPersonForm((prev) => ({ ...prev, birth_date: v }));
              }}
            />

            <div className="kosik-page__form-field">
              <label className="kosik-page__form-label">Kontaktný telefón</label>
              <input
                type="tel"
                className="kosik-page__form-input"
                placeholder="Napr. 0901 234 567"
                value={personForm.phone}
                required
                onChange={(e) =>
                  setPersonForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>
          </div>

          {/* Základná škola dieťaťa (POVINNÉ) */}
          <div className="kosik-page__form-field">
            <label className="kosik-page__form-label">Základná škola *</label>
            <Select
              placeholder="-- Vyberte základnú školu --"
              value={personForm.school_id ? String(personForm.school_id) : ""}
              onChange={(val) => {
                const found = schoolsList.find((s) => String(s.id) === String(val));
                setPersonForm((prev) => ({
                  ...prev,
                  school_id: val || "",
                  school_name: found ? found.name : "",
                }));
              }}
              options={schoolsList.map((s) => ({ value: String(s.id), label: s.name }))}
            />
          </div>

          {/* Trieda: Ročník 1-9 (Select) + Označenie triedy (A, B, C... Text) */}
          <div className="kosik-page__form-row">
            <div className="kosik-page__form-field">
              <label className="kosik-page__form-label">Trieda / Ročník (1 – 9) *</label>
              <Select
                placeholder="-- Zvoľte triedu --"
                value={personForm.class_grade ? String(personForm.class_grade) : ""}
                onChange={(val) =>
                  setPersonForm((prev) => ({ ...prev, class_grade: val || "" }))
                }
                options={[
                  { value: "1", label: "1. ročník" },
                  { value: "2", label: "2. ročník" },
                  { value: "3", label: "3. ročník" },
                  { value: "4", label: "4. ročník" },
                  { value: "5", label: "5. ročník" },
                  { value: "6", label: "6. ročník" },
                  { value: "7", label: "7. ročník" },
                  { value: "8", label: "8. ročník" },
                  { value: "9", label: "9. ročník" },
                ]}
              />
            </div>

            <div className="kosik-page__form-field">
              <label className="kosik-page__form-label">Označenie triedy (A, B, C...) *</label>
              <input
                type="text"
                className="kosik-page__form-input"
                placeholder="Napr. A, B, C..."
                maxLength={5}
                required
                value={personForm.class_letter}
                onChange={(e) =>
                  setPersonForm((prev) => ({
                    ...prev,
                    class_letter: e.target.value.toUpperCase(),
                  }))
                }
              />
            </div>
          </div>

          <div className="kosik-page__form-field">
            <label className="kosik-page__form-label">
              Poznámka / Zdravotné upozornenie (nepovinné)
            </label>
            <textarea
              className="kosik-page__form-input kosik-page__form-textarea"
              placeholder="Napr. alergie, špecifické požiadavky..."
              value={personForm.note}
              onChange={(e) =>
                setPersonForm((prev) => ({ ...prev, note: e.target.value }))
              }
            />
          </div>

          {isLoggedIn && (
            <div className="kosik-page__form-checkbox-row">
              <input
                type="checkbox"
                id="save_to_profile"
                checked={personForm.save_to_profile}
                onChange={(e) =>
                  setPersonForm((prev) => ({
                    ...prev,
                    save_to_profile: e.target.checked,
                  }))
                }
              />
              <label htmlFor="save_to_profile">
                Uložiť toto dieťa do môjho profilu pre budúce prihlášky
              </label>
            </div>
          )}

          <div className="kosik-page__form-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPersonModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button type="submit" variant="primary">
              <Check size={16} /> Priradiť dieťa k termínu
            </Button>
          </div>
        </form>
      </Modal>

      {/* ─── Modal 1: Bank Transfer Instructions ─────────────────── */}
      <Modal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        title="Platobné pokyny – Bankový prevod"
        className="kosik-page__modal-custom"
      >
        <div className="kosik-page__transfer-modal">
          <div className="kosik-page__transfer-hero-box">
            <div className="kosik-page__transfer-hero-icon">
              <Building2 size={28} />
            </div>
            <div>
              <h4 className="kosik-page__transfer-hero-title">
                Suma na úhradu: {cartTotal.toFixed(2)} €
              </h4>
              <p className="kosik-page__transfer-hero-sub">
                Pre dokončenie prihlášky pošlite platbu na náš bankový účet s
                uvedeným variabilným symbolom.
              </p>
            </div>
          </div>

          {/* Table of payment credentials */}
          <div className="kosik-page__payment-data-table">
            <div className="kosik-page__payment-data-row">
              <span className="kosik-page__payment-data-label">
                Číslo účtu (IBAN):
              </span>
              <div className="kosik-page__payment-data-val-wrap">
                <strong className="kosik-page__payment-data-val">
                  {bankDetails.iban}
                </strong>
                <button
                  type="button"
                  className={`kosik-page__copy-pill ${copiedField === "iban"
                    ? "kosik-page__copy-pill--copied"
                    : ""
                    }`}
                  onClick={() => handleCopyText(bankDetails.iban, "iban")}
                >
                  {copiedField === "iban" ? (
                    <>
                      <Check size={12} /> Skopírované
                    </>
                  ) : (
                    <>
                      <Copy size={12} /> Kopírovať
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="kosik-page__payment-data-row">
              <span className="kosik-page__payment-data-label">
                Variabilný symbol:
              </span>
              <div className="kosik-page__payment-data-val-wrap">
                <strong className="kosik-page__payment-data-val kosik-page__payment-data-val--vs">
                  {currentVariableSymbol}
                </strong>
                <button
                  type="button"
                  className={`kosik-page__copy-pill ${copiedField === "vs" ? "kosik-page__copy-pill--copied" : ""
                    }`}
                  onClick={() => handleCopyText(currentVariableSymbol, "vs")}
                >
                  {copiedField === "vs" ? (
                    <>
                      <Check size={12} /> Skopírované
                    </>
                  ) : (
                    <>
                      <Copy size={12} /> Kopírovať
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="kosik-page__payment-data-row">
              <span className="kosik-page__payment-data-label">
                Presná suma:
              </span>
              <div className="kosik-page__payment-data-val-wrap">
                <strong className="kosik-page__payment-data-val">
                  {cartTotal.toFixed(2)} €
                </strong>
                <button
                  type="button"
                  className={`kosik-page__copy-pill ${copiedField === "amount"
                    ? "kosik-page__copy-pill--copied"
                    : ""
                    }`}
                  onClick={() =>
                    handleCopyText(cartTotal.toFixed(2), "amount")
                  }
                >
                  {copiedField === "amount" ? (
                    <>
                      <Check size={12} /> Skopírované
                    </>
                  ) : (
                    <>
                      <Copy size={12} /> Kopírovať
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="kosik-page__payment-data-row">
              <span className="kosik-page__payment-data-label">
                Konštantný symbol:
              </span>
              <strong className="kosik-page__payment-data-val">
                {bankDetails.constantSymbol}
              </strong>
            </div>

            <div className="kosik-page__payment-data-row">
              <span className="kosik-page__payment-data-label">Príjemca:</span>
              <span className="kosik-page__payment-data-val">
                {bankDetails.recipientName} ({bankDetails.bankName})
              </span>
            </div>

            <div className="kosik-page__payment-data-row">
              <span className="kosik-page__payment-data-label">
                Správa pre prijímateľa:
              </span>
              <div className="kosik-page__payment-data-val-wrap">
                <span className="kosik-page__payment-data-val">
                  {recipientMessage}
                </span>
                <button
                  type="button"
                  className={`kosik-page__copy-pill ${copiedField === "msg" ? "kosik-page__copy-pill--copied" : ""
                    }`}
                  onClick={() => handleCopyText(recipientMessage, "msg")}
                >
                  {copiedField === "msg" ? (
                    <>
                      <Check size={12} /> Skopírované
                    </>
                  ) : (
                    <>
                      <Copy size={12} /> Kopírovať
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Pay by square notice / QR placeholder box */}
          <div className="kosik-page__qr-info-card">
            <div className="kosik-page__qr-box">
              <QrCode size={40} className="kosik-page__qr-icon" />
              <span className="kosik-page__qr-badge">PAY by square</span>
            </div>
            <div className="kosik-page__qr-details">
              <strong>Rýchla platba cez mobilný banking</strong>
              <p>
                Otvorte si aplikáciu vašej banky a naskenujte platobné údaje
                alebo zadajte IBAN a variabilný symbol ručne.
              </p>
              <div className="kosik-page__qr-warning">
                <AlertTriangle size={14} />
                <span>
                  QR kód funguje iba pre <strong>SLSP</strong> a{" "}
                  <strong>Tatra banku</strong>. V inej banke zadajte údaje ručne.
                </span>
              </div>
            </div>
          </div>

          {/* Copy all button */}
          <button
            type="button"
            className="kosik-page__copy-all-btn"
            onClick={() => {
              const fullDetails = `IBAN: ${bankDetails.iban}\nVS: ${currentVariableSymbol}\nSuma: ${cartTotal.toFixed(
                2
              )} EUR\nPríjemca: ${bankDetails.recipientName}\nSpráva: ${recipientMessage}`;
              handleCopyText(fullDetails, "all");
            }}
          >
            {copiedField === "all" ? (
              <>
                <Check size={16} /> Všetky údaje skopírované do schránky!
              </>
            ) : (
              <>
                <Copy size={16} /> Kopírovať všetky platobné údaje naraz
              </>
            )}
          </button>

          <div className="kosik-page__form-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setTransferModalOpen(false)}
            >
              Späť do košíka
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => handleConfirmOrder("transfer")}
            >
              <Check size={16} /> Záväzne potvrdiť prihlášku
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── Modal 3: Card Payment (Stripe / Online) ──────────────── */}
      {cardModalOpen && (
        <StripePaymentModal
          isOpen={cardModalOpen}
          onClose={() => setCardModalOpen(false)}
          orderPayload={getOrderPayload("card")}
          amount={cartTotal}
          currency="EUR"
          title="Platba kartou online – Stripe"
          onSuccess={(completedData) => {
            if (typeof refreshCredits === "function") {
              refreshCredits();
            }
            const orderData = {
              ...completedData,
              orderId: completedData.orderId,
              dbOrderId: completedData.dbOrderId,
              totalAmount: completedData.totalAmount || cartTotal,
              customerEmail: userEmail,
              paymentMethod: "card",
              status: "paid",
            };
            setCompletedOrderData(orderData);
            setCardModalOpen(false);
            clearCart();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </div>
  );
}

export default KosikPage;
